import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { getEntitlements } from '@/utils/entitlements';
import { PlanBadge } from '@/components/subscription/PlanBadge';
import {
  Shield, Sparkles, ExternalLink, ArrowRight, Check, Users,
  Calendar, CreditCard, ChevronRight, Loader2
} from 'lucide-react';

export default function HomeschoolSubscriptionPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, userProfile, isLoading: authLoading, refreshUserProfile } = useAuth();

  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [childrenCount, setChildrenCount] = useState<number>(0);
  const [facilityName, setFacilityName] = useState<string>('');
  const [billingInterval, setBillingInterval] = useState<'month' | '6month' | 'year'>('month');

  // Unified entitlements
  const { elite, standard, isPaid, plan } = getEntitlements(userProfile);
  const isParent = userProfile?.facility_role === 'admin' && !!userProfile?.facility_id;
  const hasActivePlan = isPaid;

  // Fetch children count and facility details if parent has a facility
  useEffect(() => {
    if (!user || !userProfile?.facility_id) return;

    async function loadFacilityData() {
      try {
        const { data: fac } = await supabase
          .from('facilities')
          .select('name, total_seats')
          .eq('id', userProfile!.facility_id!)
          .maybeSingle();

        if (fac?.name) setFacilityName(fac.name);

        const { count } = await supabase
          .from('users')
          .select('id', { count: 'exact', head: true })
          .eq('facility_id', userProfile!.facility_id!)
          .eq('facility_role', 'member');

        setChildrenCount(count || 0);
      } catch (err) {
        console.warn('Error fetching facility details:', err);
      }
    }

    loadFacilityData();
  }, [user, userProfile]);

  // Webhook-race guard: Stripe posts the webhook seconds after redirect.
  // Refresh immediately on mount and once more after 10 s to capture late webhooks.
  useEffect(() => {
    refreshUserProfile();
    const t = setTimeout(refreshUserProfile, 10000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleOpenPortal() {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('billing', {
        body: { action: 'portal' },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Portal redirect error:', err);
      alert('Unable to open billing portal. Please verify your subscription or contact support.');
    } finally {
      setPortalLoading(false);
    }
  }

  async function startCheckout(planKey: string, destination: 'homeschool' | 'individual', seats: number) {
    setCheckoutLoading(planKey);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        navigate(`/homeschool/enrol?plan=${planKey}`);
        return;
      }

      const { data, error } = await supabase.functions.invoke('billing', {
        body: {
          action: 'checkout',
          plan: planKey,
          interval: billingInterval,
          destination: destination,
          facility_id: userProfile?.facility_id ?? undefined,
          quantity: Math.max(seats, 1),   // FIX: was metadata-only; billing fn defaulted to 1 seat
          metadata: { seats: String(Math.max(seats, 1)) },
          ref_code: (typeof localStorage !== 'undefined' ? localStorage.getItem('sage_affiliate_ref') : null) || undefined,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Unable to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  }

  async function handleEnrol(planKey: string) {
    // 1. Active subscriber -> portal (never double-bill)
    if (hasActivePlan) {
      await handleOpenPortal();
      return;
    }
    // 2 & 3. No plan -> the wizard handles BOTH cases:
    //   no children yet (account+children+checkout) AND existing unpaid facility
    //   holders (children stepper pre-seeded with existing kids, 50% sibling
    //   discount, quantity = total children). The old case-3 direct checkout
    //   billed 1 seat with no sibling coupon and no way to add children.
    navigate(`/homeschool/enrol?mode=child&plan=${planKey}`);
  }

  const planName = !hasActivePlan
    ? 'Free / Not Subscribed'
    : plan === 'edu_bundle'
      ? 'Education + Elite Academy Bundle'
      : plan === 'edu_elite'
        ? 'Still Waters Elite Academy'
        : plan === 'homeschool_single'
          ? 'Standard Homeschool Academy'
          : 'Premium Member';

  const planTag = plan === 'edu_bundle' ? '💎' : plan === 'edu_elite' ? '👑' : '📘';

  const renewalDate = userProfile?.subscription_end_date
    ? new Date(userProfile.subscription_end_date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  // Price calculations
  const [planRows, setPlanRows] = useState<Record<string, any>>({});
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('subscription_plans')
        .select('plan_key, price_monthly, price_six_monthly, price_yearly')
        .in('plan_key', ['homeschool_single', 'edu_elite', 'edu_bundle'])
        .eq('is_active', true);
      if (data) setPlanRows(Object.fromEntries((data as any[]).map(p => [p.plan_key, p])));
    })();
  }, []);

  const p = (k: string) => planRows[k];
  const prices = {
    standard: billingInterval === 'year' ? Math.round((p('homeschool_single')?.price_yearly ?? 336) / 12)
      : billingInterval === '6month' ? Math.round((p('homeschool_single')?.price_six_monthly ?? 189) / 6)
      : p('homeschool_single')?.price_monthly ?? 35,
    elite: billingInterval === 'year' ? Math.round((p('edu_elite')?.price_yearly ?? 432) / 12)
      : billingInterval === '6month' ? Math.round((p('edu_elite')?.price_six_monthly ?? 243) / 6)
      : p('edu_elite')?.price_monthly ?? 45,
    bundle: billingInterval === 'year' ? Math.round((p('edu_bundle')?.price_yearly ?? 565) / 12)
      : billingInterval === '6month' ? Math.round((p('edu_bundle')?.price_six_monthly ?? 318) / 6)
      : p('edu_bundle')?.price_monthly ?? 59,
  };
  const periodSubtitle = {
    standard: billingInterval === 'year' ? `billed $${p('homeschool_single')?.price_yearly ?? 336} annually (save 20%)`
      : billingInterval === '6month' ? `billed $${p('homeschool_single')?.price_six_monthly ?? 189} every 6 months (save 10%)` : 'billed monthly',
    elite: billingInterval === 'year' ? `billed $${p('edu_elite')?.price_yearly ?? 432} annually (save 20%)`
      : billingInterval === '6month' ? `billed $${p('edu_elite')?.price_six_monthly ?? 243} every 6 months (save 10%)` : 'billed monthly',
    bundle: billingInterval === 'year' ? `billed $${p('edu_bundle')?.price_yearly ?? 565} annually (save 20%)`
      : billingInterval === '6month' ? `billed $${p('edu_bundle')?.price_six_monthly ?? 318} every 6 months (save 10%)` : 'billed monthly',
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#080B11] text-white font-sans">
      {/* HEADER */}
      <div style={{ background: 'linear-gradient(135deg, #0A0D17 0%, #121829 100%)' }} className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <button
            onClick={() => navigate('/homeschool/parent')}
            className="text-white/60 hover:text-white text-sm mb-4 inline-flex items-center gap-1.5 transition-colors"
          >
            ← Back to Parent Dashboard
          </button>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[11px] font-bold tracking-widest text-[#D4A843] uppercase bg-[#D4A843]/10 px-2.5 py-0.5 rounded-full border border-[#D4A843]/20">
              Still Waters Academy
            </span>
            <PlanBadge userProfile={userProfile} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
            Subscription & Billing Management
          </h1>
          <p className="text-white/60 text-sm sm:text-base max-w-xl">
            View your current plan entitlements, track family seat allocations, and manage your payment details seamlessly via Stripe.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* ACTIVE STATUS CARD */}
        {hasActivePlan ? (
          <div className="bg-[#0F1422] rounded-2xl border border-white/10 p-6 sm:p-8 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#D4A843]/10 to-transparent rounded-full blur-3xl pointer-events-none" />

            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 relative z-10">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{planTag}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{planName}</h2>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Active
                      </span>
                    </div>
                    {facilityName && (
                      <p className="text-xs text-white/50 mt-0.5">Family Hub: {facilityName}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                  <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                    <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-[#D4A843]" />
                      Children
                    </div>
                    <div className="text-base font-bold text-white mt-1">
                      {childrenCount} {childrenCount === 1 ? 'Child' : 'Children'}
                    </div>
                  </div>

                  {renewalDate && (
                    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                      <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-[#D4A843]" />
                        Next Billing
                      </div>
                      <div className="text-base font-bold text-white mt-1">
                        {renewalDate}
                      </div>
                    </div>
                  )}

                  <div className="bg-white/5 rounded-xl p-3 border border-white/5 col-span-2 sm:col-span-1">
                    <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-[#D4A843]" />
                      Proration
                    </div>
                    <div className="text-base font-bold text-emerald-400 mt-1">
                      Auto Prorated
                    </div>
                  </div>
                </div>
              </div>

              {/* ACTION BUTTON */}
              <div className="sm:self-center flex flex-col gap-3 min-w-[220px]">
                <button
                  onClick={handleOpenPortal}
                  disabled={portalLoading}
                  className="w-full bg-[#D4A843] hover:bg-[#c49833] text-black font-bold py-3 px-5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                >
                  {portalLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Opening Portal...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Manage Subscription
                    </>
                  )}
                </button>
                <p className="text-[11px] text-white/40 text-center leading-relaxed">
                  Upgrade, downgrade, update payment card, or cancel anytime in Stripe Portal.
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* NOT SUBSCRIBED STATE */
          <div className="bg-[#0F1422] rounded-2xl border border-white/10 p-6 sm:p-8 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center sm:text-left">
              <span className="inline-block text-xs font-bold uppercase tracking-wider text-amber-400 bg-amber-400/10 px-2.5 py-0.5 rounded-full border border-amber-400/20">
                No Active Plan
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-white">{t('homeschool.ready_to_begin_your_children_education')}</h2>
              <p className="text-white/60 text-sm max-w-lg">
                Enrol your children to unlock structured AI lessons across 84 core subjects, 30 elite leadership courses, quizzes, and automated progress tracking.
              </p>
            </div>
            <button
              onClick={() => handleEnrol('homeschool_single')}
              disabled={!!checkoutLoading}
              className="bg-[#D4A843] hover:bg-[#c49833] text-black font-bold py-3.5 px-6 rounded-xl transition-all flex items-center gap-2 shadow-lg shrink-0 disabled:opacity-50"
            >
              {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enrol Your Family
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* PLAN OVERVIEW / UPGRADE REFERENCE */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-white">{t('homeschool.academy_curriculum_tiers')}</h3>
              <p className="text-white/50 text-xs mt-0.5">
                Choose a plan for your family. Subscriptions include instant access to subjects and quizzes.
              </p>
            </div>

            {/* BILLING INTERVAL SELECTOR */}
            <div className="flex items-center bg-[#0F1422] p-1 rounded-xl border border-white/10 self-start sm:self-auto">
              <button
                onClick={() => setBillingInterval('month')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  billingInterval === 'month'
                    ? 'bg-[#D4A843] text-black shadow-sm'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('6month')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
                  billingInterval === '6month'
                    ? 'bg-[#D4A843] text-black shadow-sm'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                6 Months
                <span className="text-[10px] px-1 rounded bg-emerald-500/20 text-emerald-400 font-bold">-10%</span>
              </button>
              <button
                onClick={() => setBillingInterval('year')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
                  billingInterval === 'year'
                    ? 'bg-[#D4A843] text-black shadow-sm'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Annual
                <span className="text-[10px] px-1 rounded bg-emerald-500/20 text-emerald-400 font-bold">2 Mo Free</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Standard */}
            <div className={`rounded-2xl border p-6 bg-[#0F1422] flex flex-col justify-between ${plan === 'homeschool_single' ? 'border-[#D4A843] shadow-md' : 'border-white/10'}`}>
              <div>
                <div className="text-2xl mb-2">📘</div>
                <h4 className="font-bold text-white text-base">{t('homeschool.standard_homeschool')}</h4>
                <div className="text-2xl font-black text-white mt-2">
                  ${prices.standard}
                  <span className="text-xs text-white/50 font-normal"> /mo</span>
                </div>
                <p className="text-[11px] text-[#D4A843] mt-0.5">{periodSubtitle.standard}</p>
                <p className="text-xs text-white/50 mt-2 mb-4">{t('homeschool.complete_84_subject_curriculum_designed_for')}</p>
                <ul className="space-y-2 text-xs text-white/70">
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> 84 core academic subjects</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Age-adaptive AI tutoring</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Progress tracking & quizzes</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Faith & Bible Study stream included</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> 50% off additional siblings</li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-white/5 text-center">
                {plan === 'homeschool_single' ? (
                  <span className="text-xs font-bold text-[#D4A843]">{t('homeschool.current_plan')}</span>
                ) : hasActivePlan ? (
                  <button onClick={handleOpenPortal} className="text-xs text-white/50 hover:text-white font-medium underline">
                    Switch in Stripe Portal
                  </button>
                ) : (
                  <button
                    onClick={() => handleEnrol('homeschool_single')}
                    disabled={checkoutLoading === 'homeschool_single'}
                    className="w-full py-2.5 rounded-xl bg-[#D4A843] hover:bg-[#c49833] text-black text-xs font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {checkoutLoading === 'homeschool_single' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Enrol at ${prices.standard}/mo →
                  </button>
                )}
              </div>
            </div>

            {/* Elite */}
            <div className={`rounded-2xl border p-6 bg-[#0F1422] flex flex-col justify-between relative ${plan === 'edu_elite' ? 'border-[#D4A843] shadow-md' : 'border-[#D4A843]/40'}`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A843] text-black text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                Leadership
              </div>
              <div>
                <div className="text-2xl mb-2">👑</div>
                <h4 className="font-bold text-white text-base">Still Waters Elite Academy</h4>
                <div className="text-2xl font-black text-white mt-2">
                  ${prices.elite}
                  <span className="text-xs text-white/50 font-normal"> /mo</span>
                </div>
                <p className="text-[11px] text-[#D4A843] mt-0.5">{periodSubtitle.elite}</p>
                <p className="text-xs text-white/50 mt-2 mb-4">30 premier leadership courses taught in top boarding schools.</p>
                <ul className="space-y-2 text-xs text-white/70">
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> 30 elite leadership courses</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Etiquette, Elocution & Grace</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Capital Markets & Logic</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Rhetoric & Formal Debate</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Faith & Bible Study stream included</li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-white/5 text-center">
                {plan === 'edu_elite' ? (
                  <span className="text-xs font-bold text-[#D4A843]">{t('homeschool.current_plan')}</span>
                ) : hasActivePlan ? (
                  <button onClick={handleOpenPortal} className="text-xs text-white/50 hover:text-white font-medium underline">
                    Switch in Stripe Portal
                  </button>
                ) : (
                  <button
                    onClick={() => handleEnrol('edu_elite')}
                    disabled={checkoutLoading === 'edu_elite'}
                    className="w-full py-2.5 rounded-xl bg-[#D4A843] hover:bg-[#c49833] text-black text-xs font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {checkoutLoading === 'edu_elite' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Enrol at ${prices.elite}/mo →
                  </button>
                )}
              </div>
            </div>

            {/* Bundle */}
            <div className={`rounded-2xl border p-6 bg-[#0F1422] flex flex-col justify-between ${plan === 'edu_bundle' ? 'border-[#D4A843] shadow-md' : 'border-white/10'}`}>
              <div>
                <div className="text-2xl mb-2">💎</div>
                <h4 className="font-bold text-white text-base">Education + Elite Bundle</h4>
                <div className="text-2xl font-black text-white mt-2">
                  ${prices.bundle}
                  <span className="text-xs text-white/50 font-normal"> /mo</span>
                </div>
                <p className="text-[11px] text-[#D4A843] mt-0.5">{periodSubtitle.bundle}</p>
                <p className="text-xs text-white/50 mt-2 mb-4">{t('homeschool.the_complete_academic_and_leadership_suite')}</p>
                <ul className="space-y-2 text-xs text-white/70">
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> All 84 standard subjects</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> All 30 elite leadership courses</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Full Faith & Bible stream included</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Priority AI responsiveness</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4A843]" /> Comprehensive parent reports</li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-white/5 text-center">
                {plan === 'edu_bundle' ? (
                  <span className="text-xs font-bold text-[#D4A843]">{t('homeschool.current_plan')}</span>
                ) : hasActivePlan ? (
                  <button onClick={handleOpenPortal} className="text-xs text-white/50 hover:text-white font-medium underline">
                    Switch in Stripe Portal
                  </button>
                ) : (
                  <button
                    onClick={() => handleEnrol('edu_bundle')}
                    disabled={checkoutLoading === 'edu_bundle'}
                    className="w-full py-2.5 rounded-xl bg-[#D4A843] hover:bg-[#c49833] text-black text-xs font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {checkoutLoading === 'edu_bundle' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Enrol at ${prices.bundle}/mo →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* QUICK MANAGEMENT LINKS */}
        <div className="bg-[#0F1422] rounded-2xl border border-white/10 p-5 sm:p-6">
          <h4 className="font-bold text-sm text-white mb-3">{t('homeschool.family_account_management')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/homeschool/parent')}
              className="text-left p-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all flex items-center justify-between"
            >
              <div>
                <div className="text-xs font-bold text-white">{t('homeschool.add_or_manage_children')}</div>
                <div className="text-[11px] text-white/50 mt-0.5">{t('homeschool.enrol_new_children_into_your_family')}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-white/40" />
            </button>

            <button
              onClick={handleOpenPortal}
              disabled={portalLoading}
              className="text-left p-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all flex items-center justify-between"
            >
              <div>
                <div className="text-xs font-bold text-white">Stripe Customer Portal</div>
                <div className="text-[11px] text-white/50 mt-0.5">{t('homeschool.update_payment_card_view_invoice_receipts')}</div>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-white/40" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
